import { UserProfile } from '../types';

export interface ActiveEnrollmentInfo {
  classNum: number;
  subjectName: 'Science' | 'Mathematics' | 'Social Science';
  courseId: string;
}

export interface StudentAccessInfo {
  isPremiumStudent: boolean;
  purchasedClass: string; // e.g. "Class 6"
  purchasedClassNum: number; // e.g. 6
  purchasedSubjects: ('Science' | 'Mathematics' | 'Social Science')[]; // Subjects purchased for current class
  hasSingleSubject: boolean;
  singleSubject?: 'Science' | 'Mathematics' | 'Social Science';
  activeEnrollmentsList: ActiveEnrollmentInfo[];
}

/**
 * Parses any course identifier or title to extract class number and subject name.
 */
export function parseCourseInfo(courseIdOrTitle: string): { classNum: number | null; subjectName: 'Science' | 'Mathematics' | 'Social Science' | null } {
  if (!courseIdOrTitle) return { classNum: null, subjectName: null };
  const str = String(courseIdOrTitle).toLowerCase();

  // Determine Class Number
  let classNum: number | null = null;
  if (str.includes('class 6') || str.includes('class_6') || str.includes('c6') || str.startsWith('6-') || str.includes('_6_') || str === '6') {
    classNum = 6;
  } else if (str.includes('class 7') || str.includes('class_7') || str.includes('c7') || str.startsWith('7-') || str.includes('_7_') || str === '7') {
    classNum = 7;
  } else if (str.includes('class 8') || str.includes('class_8') || str.includes('c8') || str.startsWith('8-') || str.includes('_8_') || str === '8') {
    classNum = 8;
  } else if (str.includes('class 9') || str.includes('class_9') || str.includes('c9') || str.startsWith('9-') || str.includes('_9_') || str === '9') {
    classNum = 9;
  } else if (str.includes('class 10') || str.includes('c10')) {
    classNum = 10;
  } else if (str.includes('class 11') || str.includes('c11')) {
    classNum = 11;
  } else if (str.includes('class 12') || str.includes('c12')) {
    classNum = 12;
  } else {
    const match = str.match(/\d+/);
    if (match) classNum = parseInt(match[0], 10);
  }

  // Determine Subject
  let subjectName: 'Science' | 'Mathematics' | 'Social Science' | null = null;
  const isSocial = str.includes('social') || str.includes('soc') || str.includes('ss1') || str.includes('c6ss') || str.includes('c7ss') || str.includes('c8ss') || str === '3';
  const isMath = str.includes('math') || str.includes('m1') || str.includes('c6m') || str.includes('c7m') || str.includes('c8m') || str === '2';
  const isSci = (str.includes('science') || str.includes('sci') || str.includes('s1') || str.includes('c6s') || str.includes('c7s') || str.includes('c8s') || str === '1') && !isSocial;

  if (isSocial) subjectName = 'Social Science';
  else if (isMath) subjectName = 'Mathematics';
  else if (isSci) subjectName = 'Science';

  return { classNum, subjectName };
}

/**
 * Checks if a student has an ACTIVE non-expired enrollment for a SPECIFIC Class + Subject.
 */
export function isCourseEnrolled(
  user: UserProfile | null | undefined,
  classIdOrNum: number | string,
  subjectIdOrName: string
): boolean {
  if (!user) return false;

  // Admins and Teachers have full access
  const role = String(user.role || '').toLowerCase();
  if (role === 'admin' || role === 'teacher' || role === 'super admin') {
    return true;
  }

  // Target class number
  let targetClassNum = 6;
  if (typeof classIdOrNum === 'number') {
    targetClassNum = classIdOrNum;
  } else {
    const match = String(classIdOrNum).match(/\d+/);
    targetClassNum = match ? parseInt(match[0], 10) : 6;
  }

  // Target subject
  const subLower = String(subjectIdOrName).toLowerCase();
  const targetIsSocial = subLower.includes('social') || subLower.includes('soc');
  const targetIsMath = subLower.includes('math');
  const targetIsScience = (subLower.includes('science') || subLower.includes('sci')) && !targetIsSocial;

  let targetSubjectName: 'Science' | 'Mathematics' | 'Social Science' | null = null;
  if (targetIsSocial) targetSubjectName = 'Social Science';
  else if (targetIsMath) targetSubjectName = 'Mathematics';
  else if (targetIsScience) targetSubjectName = 'Science';

  if (!targetSubjectName) return false;

  // Check activeEnrollments array first if available
  if (user.activeEnrollments && user.activeEnrollments.length > 0) {
    const now = new Date();
    const hasActive = user.activeEnrollments.some(e => {
      // Must be ACTIVE and not expired
      if (e.status && e.status !== 'ACTIVE') return false;
      if (e.expiryDate && new Date(e.expiryDate) < now) return false;

      // Check combo/all
      const cidLower = String(e.courseId || '').toLowerCase();
      const titleLower = String(e.courseTitle || '').toLowerCase();
      if (cidLower.includes('combo') || cidLower.includes('all') || titleLower.includes('combo') || titleLower.includes('all')) {
        return true;
      }

      // Check classNum and subjectName match
      const info1 = parseCourseInfo(e.courseId || '');
      const info2 = parseCourseInfo(e.courseTitle || '');

      const enrolledClass = e.classNum || info1.classNum || info2.classNum;
      const enrolledSubject = e.subjectName || info1.subjectName || info2.subjectName;

      return enrolledClass === targetClassNum && enrolledSubject === targetSubjectName;
    });

    if (hasActive) return true;
  }

  // Check enrolledCourses array (string IDs e.g. ['6-sci'])
  const enrolled = user.enrolledCourses || [];
  return enrolled.some(cid => {
    const cidLower = String(cid).toLowerCase();
    if (cidLower.includes('combo') || cidLower.includes('all')) return true;

    const info = parseCourseInfo(cid);
    return info.classNum === targetClassNum && info.subjectName === targetSubjectName;
  });
}

export function getStudentAccessInfo(user: UserProfile | null | undefined): StudentAccessInfo {
  const isUserPremium = Boolean(user?.isPremium || (user?.enrolledCourses && user.enrolledCourses.length > 0) || (user?.activeEnrollments && user.activeEnrollments.length > 0));

  const rawClass = user?.currentAcademicClass || user?.currentClass || 'Class 6';
  const matchNum = rawClass.match(/\d+/);
  const classNum = matchNum ? parseInt(matchNum[0], 10) : 6;

  if (!user || user.role === 'admin' || user.role === 'teacher' || !isUserPremium) {
    return {
      isPremiumStudent: false,
      purchasedClass: `Class ${classNum}`,
      purchasedClassNum: classNum,
      purchasedSubjects: [],
      hasSingleSubject: false,
      activeEnrollmentsList: []
    };
  }

  const activeEnrollmentsList: ActiveEnrollmentInfo[] = [];
  const purchasedSubjectsSet = new Set<'Science' | 'Mathematics' | 'Social Science'>();

  // Check activeEnrollments
  if (user.activeEnrollments && user.activeEnrollments.length > 0) {
    const now = new Date();
    user.activeEnrollments.forEach(e => {
      if (e.status && e.status !== 'ACTIVE') return;
      if (e.expiryDate && new Date(e.expiryDate) < now) return;

      const info1 = parseCourseInfo(e.courseId || '');
      const info2 = parseCourseInfo(e.courseTitle || '');
      const cNum = e.classNum || info1.classNum || info2.classNum || classNum;
      const sName = e.subjectName || info1.subjectName || info2.subjectName;

      if (sName) {
        if (cNum === classNum) {
          purchasedSubjectsSet.add(sName);
        }
        activeEnrollmentsList.push({ classNum: cNum, subjectName: sName, courseId: e.courseId || '' });
      }
    });
  }

  // Fallback check enrolledCourses array
  if (activeEnrollmentsList.length === 0 && user.enrolledCourses) {
    user.enrolledCourses.forEach(cid => {
      const info = parseCourseInfo(cid);
      if (info.subjectName) {
        const cNum = info.classNum || classNum;
        if (cNum === classNum) {
          purchasedSubjectsSet.add(info.subjectName);
        }
        activeEnrollmentsList.push({ classNum: cNum, subjectName: info.subjectName, courseId: String(cid) });
      }
    });
  }

  const purchasedSubjects = Array.from(purchasedSubjectsSet);
  const hasSingleSubject = purchasedSubjects.length === 1;
  const singleSubject = hasSingleSubject ? purchasedSubjects[0] : undefined;

  return {
    isPremiumStudent: true,
    purchasedClass: `Class ${classNum}`,
    purchasedClassNum: classNum,
    purchasedSubjects,
    hasSingleSubject,
    singleSubject,
    activeEnrollmentsList
  };
}

/**
 * Checks if a student can access a specific chapter's content.
 * Allows Chapter 1 access for Classes 6, 7, 8 and subjects Science, Mathematics, Social Science.
 * Otherwise, verifies if they have a valid subscription matching BOTH class AND subject.
 */
export function canAccessContent(
  user: UserProfile | null | undefined,
  classIdOrName: string | number,
  subjectIdOrName: string,
  chapterIdOrNum: string | number
): boolean {
  // Normalize chapter number (Chapter 1 is always free)
  let chapterNum = 1;
  if (typeof chapterIdOrNum === 'number') {
    chapterNum = chapterIdOrNum;
  } else {
    const numMatch = String(chapterIdOrNum).match(/\d+/);
    chapterNum = numMatch ? parseInt(numMatch[0], 10) : 1;
  }

  // Normalize Class Number
  let classNum = 6;
  if (typeof classIdOrName === 'number') {
    classNum = classIdOrName;
  } else {
    const numMatch = String(classIdOrName).match(/\d+/);
    classNum = numMatch ? parseInt(numMatch[0], 10) : 6;
  }

  // Normalize Subject
  const normSubject = String(subjectIdOrName).toLowerCase();
  const isValidClass = classNum === 6 || classNum === 7 || classNum === 8;
  const isValidSubject = normSubject.includes('sci') || normSubject.includes('math') || normSubject.includes('soc') || normSubject.includes('social');

  // Chapter 1 of valid classes/subjects is free for all logged-in/registered students
  if (isValidClass && isValidSubject && chapterNum === 1) {
    return true;
  }

  // Chapter 2+ requires paid enrollment for THAT specific class and subject
  return isCourseEnrolled(user, classNum, subjectIdOrName);
}
